import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import VoiceCheckin from "@/components/VoiceCheckin";
import SymptomPhotoUpload from "@/components/SymptomPhotoUpload";
import NotificationSettings from "@/components/NotificationSettings";
import { 
  Heart, Activity, Pill, Calendar, Phone, FileText, Brain, 
  TrendingUp, TrendingDown, Minus, CheckCircle, Clock, AlertTriangle,
  Sparkles, Sun, Moon, Coffee, Smile, Meh, Frown, Users, 
  Plus, ChevronRight, RefreshCw, Loader2, Zap, Target, Award, Mic,
  PhoneCall, UserCheck, MapPin, CalendarClock, Bell, Settings
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getMyScheduledVisits, requestCallback, getQueueStatus } from "@/lib/api";

// Mood options - labels will be translated via t() function
const MOODS = [
  { value: "peaceful", labelKey: "mood.peaceful", icon: Sun, color: "text-blue-500" },
  { value: "happy", labelKey: "mood.happy", icon: Smile, color: "text-green-500" },
  { value: "okay", labelKey: "mood.okay", icon: Coffee, color: "text-amber-500" },
  { value: "tired", labelKey: "mood.tired", icon: Moon, color: "text-purple-500" },
  { value: "anxious", labelKey: "mood.anxious", icon: Meh, color: "text-orange-500" },
  { value: "sad", labelKey: "mood.sad", icon: Frown, color: "text-gray-500" },
];

// Symptom options - keys for translation
const SYMPTOM_KEYS = [
  { value: "Pain", key: "symptom.pain" },
  { value: "Nausea", key: "symptom.nausea" },
  { value: "Fatigue", key: "symptom.fatigue" },
  { value: "Shortness of Breath", key: "symptom.breathlessness" },
  { value: "Constipation", key: "symptom.constipation" },
  { value: "Anxiety", key: "symptom.anxiety" },
  { value: "Confusion", key: "symptom.confusion" },
  { value: "Loss of Appetite", key: "symptom.appetiteLoss" },
  { value: "Insomnia", key: "symptom.insomnia" },
  { value: "Weakness", key: "symptom.weakness" }
];

export default function CareDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [trends, setTrends] = useState(null);
  const [medications, setMedications] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinData, setCheckinData] = useState({
    overall_feeling: 3,
    pain_level: 3,
    sleep_quality: 3,
    appetite: 3,
    mobility: 3,
    mood: "okay",
    symptoms: [],
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [showVoiceCheckin, setShowVoiceCheckin] = useState(false);
  
  // Scheduled Visits State
  const [scheduledVisits, setScheduledVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  
  // Callback Request State
  const [showCallbackDialog, setShowCallbackDialog] = useState(false);
  const [callbackData, setCallbackData] = useState({
    reason: "",
    priority: "normal",
    callbackNumber: "",
    preferredTime: "asap",
    notes: ""
  });
  const [requestingCallback, setRequestingCallback] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  
  // Notification Settings State
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsRes, insightsRes, medsRes, eventsRes] = await Promise.all([
        api.get("/dashboard/quick-stats").catch(() => ({ data: null })),
        api.get("/insights").catch(() => ({ data: { insights: [] } })),
        api.get("/medications/today").catch(() => ({ data: { schedule: [] } })),
        api.get("/calendar/upcoming", { params: { days: 7 } }).catch(() => ({ data: { events: [] } }))
      ]);
      
      setStats(statsRes.data);
      setInsights(insightsRes.data?.insights || []);
      setMedications(medsRes.data?.schedule || []);
      setUpcomingEvents(eventsRes.data?.events || []);
      
      // Load trends if we have check-in data
      if (statsRes.data?.checkin?.streak_days >= 3) {
        const trendsRes = await api.get("/checkin/trends", { params: { days: 14 } }).catch(() => ({ data: null }));
        setTrends(trendsRes.data?.trends);
      }
      
      // Load scheduled visits
      loadScheduledVisits();
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  const loadScheduledVisits = async () => {
    setVisitsLoading(true);
    try {
      const data = await getMyScheduledVisits(false, 30);
      setScheduledVisits(data.visits || []);
    } catch (error) {
      console.error("Failed to load scheduled visits:", error);
    } finally {
      setVisitsLoading(false);
    }
  };
  
  const handleRequestCallback = async () => {
    if (!callbackData.reason.trim()) {
      toast.error("Please describe the reason for your call");
      return;
    }
    
    setRequestingCallback(true);
    try {
      const result = await requestCallback(
        callbackData.reason,
        callbackData.priority,
        callbackData.callbackNumber || null,
        callbackData.preferredTime,
        callbackData.notes || null
      );
      
      toast.success(result.message || "Callback requested successfully!");
      setShowCallbackDialog(false);
      setCallbackData({ reason: "", priority: "normal", callbackNumber: "", preferredTime: "asap", notes: "" });
      
      // Check queue status
      const status = await getQueueStatus();
      setQueueStatus(status);
    } catch (error) {
      console.error("Failed to request callback:", error);
      toast.error(error.response?.data?.detail || "Failed to request callback. Please try again.");
    } finally {
      setRequestingCallback(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadDashboard();
    }
  }, [user, loadDashboard]);

  const handleCheckin = async () => {
    setIsSubmitting(true);
    try {
      await api.post("/checkin", checkinData);
      toast.success("Check-in completed! Great job tracking your day.");
      setShowCheckin(false);
      loadDashboard();
    } catch (error) {
      toast.error("Failed to save check-in");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMedicationLog = async (medicationId, status = "taken") => {
    try {
      await api.post(`/medications/${medicationId}/log`, null, { params: { status } });
      toast.success("Medication logged!");
      loadDashboard();
    } catch (error) {
      toast.error("Failed to log medication");
    }
  };

  const handleGenerateInsights = async () => {
    setGeneratingInsights(true);
    try {
      const response = await api.post("/insights/generate");
      if (response.data.insights?.length > 0) {
        toast.success(`Generated ${response.data.insights.length} new insights`);
        loadDashboard();
      } else {
        toast.info("No new insights at this time. Keep tracking!");
      }
    } catch (error) {
      toast.error("Failed to generate insights");
    } finally {
      setGeneratingInsights(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "increasing") return <TrendingUp className="w-4 h-4 text-amber-500" />;
    if (trend === "decreasing") return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (!user) {
    return (
      <div className="container-max px-6 py-12 text-center">
        <Heart className="w-16 h-16 mx-auto mb-4 text-primary/30" />
        <h1 className="text-2xl font-serif mb-2">{t('dashboard.welcomeTitle', 'Welcome to Your Care Dashboard')}</h1>
        <p className="text-muted-foreground mb-6">{t('dashboard.signInPrompt', 'Sign in to access your personalized care tools')}</p>
        <Button asChild>
          <Link to="/">{t('common.getStarted', 'Get Started')}</Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-max px-6 py-12 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container-max px-6 py-8" data-testid="care-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">
            {getGreeting(t)}, {user.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadDashboard} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {/* Daily Check-in Card */}
        <Card className={`transition-all hover:shadow-md ${stats?.checkin?.completed_today ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-2xl font-bold">
                  {stats?.checkin?.completed_today ? (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  ) : (
                    <Target className="w-8 h-8 text-amber-500" />
                  )}
                </p>
                <p className="text-sm font-medium mt-1">
                  {stats?.checkin?.completed_today ? t('dashboard.checkedIn') : t('dashboard.checkin')}
                </p>
              </div>
              <div className="text-right">
                <Badge variant="outline" className="mb-1">
                  <Award className="w-3 h-3 mr-1" />
                  {stats?.checkin?.streak_days || 0} {t('dashboard.streak')}
                </Badge>
              </div>
            </div>
            {!stats?.checkin?.completed_today && (
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="flex-1"
                  onClick={() => setShowCheckin(true)}
                  data-testid="manual-checkin-btn"
                >
                  <Heart className="w-3 h-3 mr-1" />
                  {t('dashboard.manual')}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowVoiceCheckin(true)}
                  data-testid="voice-checkin-btn"
                >
                  <Mic className="w-3 h-3 mr-1" />
                  {t('dashboard.voice')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Medications Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <Pill className="w-8 h-8 text-primary mb-1" />
                <p className="text-sm font-medium">{t('dashboard.medications')}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">
                  {stats?.medications?.taken || 0}/{stats?.medications?.total_today || 0}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats?.medications?.adherence_rate || 100}% {t('dashboard.today', 'today')}
                </p>
              </div>
            </div>
            <Progress 
              value={stats?.medications?.adherence_rate || 100} 
              className="mt-2 h-1.5"
            />
          </CardContent>
        </Card>

        {/* Upcoming Events Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <Calendar className="w-8 h-8 text-primary mb-1" />
                <p className="text-sm font-medium">{t('dashboard.upcoming')}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{upcomingEvents.length}</p>
                <p className="text-xs text-muted-foreground">{t('dashboard.thisWeek', 'this week')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Care Team Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <Users className="w-8 h-8 text-primary mb-1" />
                <p className="text-sm font-medium">{t('dashboard.careTeam')}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{stats?.care_team_count || 0}</p>
                <p className="text-xs text-muted-foreground">{t('dashboard.assigned', 'assigned')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI Insights */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    {t('dashboard.insights')}
                  </CardTitle>
                  <CardDescription>{t('dashboard.insightsDescription', 'AI-powered observations based on your data')}</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleGenerateInsights}
                  disabled={generatingInsights}
                >
                  {generatingInsights ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  {t('dashboard.generateInsights')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {insights.length > 0 ? (
                <div className="space-y-3">
                  {insights.slice(0, 4).map((insight) => (
                    <div 
                      key={insight.id}
                      className={`p-4 rounded-lg border ${
                        insight.severity === 'alert' ? 'bg-red-50 border-red-200' :
                        insight.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                        'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {insight.severity === 'alert' ? (
                          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        ) : insight.severity === 'warning' ? (
                          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                        ) : (
                          <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{insight.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
                        </div>
                        {!insight.is_read && (
                          <Badge variant="secondary" className="text-xs">{t('common.new', 'New')}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('dashboard.noInsightsYet', 'No insights yet. Keep tracking your daily check-ins!')}</p>
                  <p className="text-sm mt-1">{t('dashboard.needMoreData', 'We need at least 3 days of data to generate insights.')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trends */}
          {trends && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  {t('dashboard.yourTrends', 'Your Trends')}
                </CardTitle>
                <CardDescription>{t('dashboard.trendsDescription', 'Based on your last {{count}} check-ins', { count: trends.days_tracked })}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('checkin.pain')}</span>
                      {getTrendIcon(trends.pain?.trend)}
                    </div>
                    <p className="text-2xl font-bold">{trends.pain?.average?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.avg', 'avg')} /10</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('checkin.overall', 'Feeling')}</span>
                      {getTrendIcon(trends.overall_feeling?.trend)}
                    </div>
                    <p className="text-2xl font-bold">{trends.overall_feeling?.average?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.avg', 'avg')} /5</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('checkin.sleep')}</span>
                      {getTrendIcon(trends.sleep?.trend)}
                    </div>
                    <p className="text-2xl font-bold">{trends.sleep?.average?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.avg', 'avg')} /5</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('checkin.appetite')}</span>
                      {getTrendIcon(trends.appetite?.trend)}
                    </div>
                    <p className="text-2xl font-bold">{trends.appetite?.average?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.avg', 'avg')} /5</p>
                  </div>
                </div>
                
                {/* Common Symptoms */}
                {trends.common_symptoms?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">{t('dashboard.mostReportedSymptoms', 'Most Reported Symptoms')}</p>
                    <div className="flex flex-wrap gap-2">
                      {trends.common_symptoms.slice(0, 5).map((s, i) => (
                        <Badge key={i} variant="outline">
                          {s.symptom} ({s.count})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Today's Medications */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="w-5 h-5 text-primary" />
                    {t('medications.todaySchedule')}
                  </CardTitle>
                  <CardDescription>{t('medications.trackSchedule', 'Track your medication schedule')}</CardDescription>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/medications">
                    {t('common.manage', 'Manage')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {medications.length > 0 ? (
                <div className="space-y-3">
                  {medications.map((item, i) => (
                    <div 
                      key={i}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.taken ? 'bg-green-50 border-green-200' : 'bg-card'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          item.taken ? 'bg-green-100' : 'bg-primary/10'
                        }`}>
                          {item.taken ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{item.medication?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.medication?.dosage} • {item.scheduled_time}
                          </p>
                        </div>
                      </div>
                      {!item.taken && (
                        <Button 
                          size="sm"
                          onClick={() => handleMedicationLog(item.medication?.id)}
                        >
                          {t('medications.markTaken')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Pill className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('medications.noScheduled', 'No medications scheduled')}</p>
                  <Button variant="outline" size="sm" className="mt-2" asChild>
                    <Link to="/medications">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('medications.add')}
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Scheduled Visits - NEW */}
          <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="scheduled-visits-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-primary" />
                  {t('dashboard.scheduledVisits', 'Scheduled Visits')}
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setShowNotificationSettings(true)}
                    title={t('common.settings')}
                    data-testid="notification-settings-btn"
                  >
                    <Bell className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={loadScheduledVisits}
                    disabled={visitsLoading}
                  >
                    <RefreshCw className={`w-4 h-4 ${visitsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {visitsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : scheduledVisits.length > 0 ? (
                <div className="space-y-3">
                  {scheduledVisits.slice(0, 4).map((visit) => (
                    <div 
                      key={visit.id} 
                      className={`p-3 rounded-lg border ${
                        visit.is_today ? 'bg-green-50 border-green-200' :
                        visit.is_tomorrow ? 'bg-amber-50 border-amber-200' :
                        'bg-card'
                      }`}
                      data-testid={`visit-${visit.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" 
                            style={{ backgroundColor: visit.color || '#6B7280' }}
                          />
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{visit.type_name || visit.visit_type}</p>
                            <p className="text-xs text-muted-foreground">{visit.formatted_date}</p>
                            <p className="text-xs text-muted-foreground">
                              {visit.start_time} - {visit.end_time}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {visit.is_today && (
                            <Badge className="bg-green-500 text-xs">{t('dashboard.today', 'Today')}</Badge>
                          )}
                          {visit.is_tomorrow && (
                            <Badge variant="outline" className="border-amber-400 text-amber-600 text-xs">{t('dashboard.tomorrow', 'Tomorrow')}</Badge>
                          )}
                          {visit.priority === "urgent" && (
                            <Badge variant="destructive" className="text-xs">{t('dashboard.urgent', 'Urgent')}</Badge>
                          )}
                        </div>
                      </div>
                      {visit.staff_name && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                          <UserCheck className="w-3 h-3" />
                          <span>{visit.staff_name}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {scheduledVisits.length > 4 && (
                    <p className="text-xs text-muted-foreground text-center">
                      +{scheduledVisits.length - 4} {t('dashboard.moreVisits', 'more visits scheduled')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <CalendarClock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('dashboard.noVisits', 'No upcoming visits scheduled')}</p>
                  <p className="text-xs mt-1">{t('dashboard.teamWillSchedule', 'Your care team will schedule visits as needed')}</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Request Callback - NEW */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-transparent" data-testid="request-callback-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <PhoneCall className="w-5 h-5" />
                {t('dashboard.needToTalk', 'Need to Talk?')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.requestCallbackDesc', 'Request a callback from our care team')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {queueStatus?.in_queue ? (
                <div className="p-4 bg-blue-100 rounded-lg text-center">
                  <p className="font-medium text-blue-800">{t('dashboard.inQueue', "You're in the queue!")}</p>
                  <p className="text-sm text-blue-600 mt-1">
                    {t('dashboard.queuePosition', 'Position')}: {queueStatus.position} • {t('dashboard.estWait', 'Est. wait')}: {queueStatus.estimated_wait_minutes || t('dashboard.soon', 'Soon')} min
                  </p>
                </div>
              ) : (
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={() => setShowCallbackDialog(true)}
                  data-testid="request-callback-btn"
                >
                  <PhoneCall className="w-4 h-4 mr-2" />
                  {t('dashboard.requestCallback', 'Request Callback')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard.quickActions')}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="h-auto py-3 flex-col gap-1" asChild>
                <Link to="/chat">
                  <Brain className="w-5 h-5" />
                  <span className="text-xs">{t('dashboard.askAI')}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex-col gap-1" asChild>
                <Link to="/journal">
                  <FileText className="w-5 h-5" />
                  <span className="text-xs">{t('nav.journal')}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex-col gap-1" asChild>
                <Link to="/symptoms">
                  <Activity className="w-5 h-5" />
                  <span className="text-xs">{t('nav.symptoms')}</span>
                </Link>
              </Button>
              <SymptomPhotoUpload patientId={user?.id} patientName={user?.name} />
            </CardContent>
          </Card>

          {/* Emergency Contacts */}
          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                <Phone className="w-5 h-5" />
                {t('emergency.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <a 
                  href="tel:703-578-7108"
                  className="flex items-center justify-between p-3 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-red-700">{t('emergency.hospice')}</p>
                    <p className="text-sm text-red-600">{t('emergency.nurseLine')}</p>
                  </div>
                  <Phone className="w-5 h-5 text-red-500" />
                </a>
                <a 
                  href="tel:911"
                  className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                >
                  <div>
                    <p className="font-medium">{t('dashboard.emergency', 'Emergency')}</p>
                    <p className="text-sm text-muted-foreground">{t('emergency.call911')}</p>
                  </div>
                  <Phone className="w-5 h-5" />
                </a>
              </div>
              <Button variant="outline" className="w-full mt-3" size="sm" asChild>
                <Link to="/contacts">{t('dashboard.manageContacts', 'Manage Contacts')}</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t('dashboard.upcoming')}</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/calendar">{t('dashboard.viewAll', 'View All')}</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.slice(0, 3).map((event) => (
                    <div key={event.id} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{event.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(event.start_time).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('dashboard.noUpcomingEvents', 'No upcoming events')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Daily Check-in Dialog */}
      <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              {t('checkin.title')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Overall Feeling */}
            <div className="space-y-3">
              <Label>{t('checkin.overall')}</Label>
              <div className="flex justify-between">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setCheckinData(prev => ({ ...prev, overall_feeling: val }))}
                    className={`w-12 h-12 rounded-full border-2 transition-all ${
                      checkinData.overall_feeling === val 
                        ? 'border-primary bg-primary text-white' 
                        : 'border-muted hover:border-primary/50'
                    }`}
                  >
                    {val}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('checkin.veryPoor', 'Very Poor')}</span>
                <span>{t('checkin.excellent', 'Excellent')}</span>
              </div>
            </div>

            {/* Pain Level */}
            <div className="space-y-3">
              <Label>{t('checkin.pain')}: {checkinData.pain_level}/10</Label>
              <Slider
                value={[checkinData.pain_level]}
                onValueChange={([val]) => setCheckinData(prev => ({ ...prev, pain_level: val }))}
                max={10}
                step={1}
                className="py-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('checkin.noPain', 'No Pain')}</span>
                <span>{t('checkin.severe', 'Severe')}</span>
              </div>
            </div>

            {/* Sleep Quality */}
            <div className="space-y-3">
              <Label>{t('checkin.sleep')}</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setCheckinData(prev => ({ ...prev, sleep_quality: val }))}
                    className={`flex-1 py-2 rounded border transition-all ${
                      checkinData.sleep_quality === val 
                        ? 'border-primary bg-primary/10' 
                        : 'border-muted hover:border-primary/50'
                    }`}
                  >
                    {val}
                  </button>
                ))}
              </div>
            </div>

            {/* Mood */}
            <div className="space-y-3">
              <Label>{t('checkin.mood')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {MOODS.map((mood) => {
                  const Icon = mood.icon;
                  return (
                    <button
                      key={mood.value}
                      type="button"
                      onClick={() => setCheckinData(prev => ({ ...prev, mood: mood.value }))}
                      className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                        checkinData.mood === mood.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${mood.color}`} />
                      <span className="text-xs">{t(mood.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Symptoms */}
            <div className="space-y-3">
              <Label>{t('checkin.symptoms')}</Label>
              <div className="flex flex-wrap gap-2">
                {SYMPTOM_KEYS.map((symptom) => (
                  <button
                    key={symptom.value}
                    type="button"
                    onClick={() => {
                      setCheckinData(prev => ({
                        ...prev,
                        symptoms: prev.symptoms.includes(symptom.value)
                          ? prev.symptoms.filter(s => s !== symptom.value)
                          : [...prev.symptoms, symptom.value]
                      }));
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      checkinData.symptoms.includes(symptom.value)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-muted hover:border-primary/50'
                    }`}
                  >
                    {t(symptom.key)}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('checkin.notes')}</Label>
              <Textarea
                placeholder={t('checkin.notesPlaceholder', 'How are you really feeling today?')}
                value={checkinData.notes}
                onChange={(e) => setCheckinData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>

            <Button 
              className="w-full" 
              onClick={handleCheckin}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {t('checkin.complete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Check-in Dialog */}
      <VoiceCheckin 
        open={showVoiceCheckin} 
        onOpenChange={setShowVoiceCheckin}
        onComplete={loadDashboard}
      />
      
      {/* Request Callback Dialog */}
      <Dialog open={showCallbackDialog} onOpenChange={setShowCallbackDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-blue-600" />
              {t('callback.title', 'Request a Callback')}
            </DialogTitle>
            <DialogDescription>
              {t('callback.description', 'Our care team will call you back as soon as possible')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="callback-reason">{t('callback.whatHelp', 'What do you need help with?')} *</Label>
              <Textarea
                id="callback-reason"
                placeholder={t('callback.describeConcern', 'Describe your concern or question...')}
                value={callbackData.reason}
                onChange={(e) => setCallbackData(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                data-testid="callback-reason-input"
              />
            </div>
            
            {/* Priority */}
            <div className="space-y-2">
              <Label>{t('callback.howUrgent', 'How urgent is this?')}</Label>
              <Select 
                value={callbackData.priority}
                onValueChange={(v) => setCallbackData(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger data-testid="callback-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      {t('callback.normal', 'Normal - Within a few hours')}
                    </span>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500" />
                      {t('callback.urgent', 'Urgent - Within 30 minutes')}
                    </span>
                  </SelectItem>
                  <SelectItem value="emergency">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      {t('callback.emergencyOption', 'Emergency - Call me immediately')}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Callback Number */}
            <div className="space-y-2">
              <Label htmlFor="callback-number">{t('callback.phoneNumber', 'Phone number')} ({t('common.optional', 'optional')})</Label>
              <Input
                id="callback-number"
                type="tel"
                placeholder={t('callback.useRegistered', 'Use my registered number')}
                value={callbackData.callbackNumber}
                onChange={(e) => setCallbackData(prev => ({ ...prev, callbackNumber: e.target.value }))}
                data-testid="callback-number-input"
              />
              <p className="text-xs text-muted-foreground">{t('callback.leaveBlank', 'Leave blank to use your registered phone number')}</p>
            </div>
            
            {/* Preferred Time */}
            <div className="space-y-2">
              <Label>{t('callback.bestTime', 'Best time to call')}</Label>
              <Select
                value={callbackData.preferredTime}
                onValueChange={(v) => setCallbackData(prev => ({ ...prev, preferredTime: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asap">{t('callback.asap', 'As soon as possible')}</SelectItem>
                  <SelectItem value="morning">{t('callback.morning', 'Morning (8am - 12pm)')}</SelectItem>
                  <SelectItem value="afternoon">{t('callback.afternoon', 'Afternoon (12pm - 5pm)')}</SelectItem>
                  <SelectItem value="evening">{t('callback.evening', 'Evening (5pm - 8pm)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Emergency Warning */}
            {callbackData.priority === "emergency" && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-700">{t('callback.lifeThreatening', 'For life-threatening emergencies:')}</p>
                    <p className="text-red-600">{t('callback.call911', 'Please call 911 immediately')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCallbackDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRequestCallback}
              disabled={requestingCallback || !callbackData.reason.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-callback-btn"
            >
              {requestingCallback ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PhoneCall className="w-4 h-4 mr-2" />
              )}
              {t('dashboard.requestCallback', 'Request Callback')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Notification Settings Dialog */}
      <NotificationSettings 
        open={showNotificationSettings} 
        onOpenChange={setShowNotificationSettings}
      />
    </div>
  );
}

function getGreeting(t) {
  const hour = new Date().getHours();
  if (hour < 12) return t('dashboard.greeting.morning');
  if (hour < 17) return t('dashboard.greeting.afternoon');
  return t('dashboard.greeting.evening');
}
